import React, { useEffect, useState } from "react";
import useApi from "../../../CustomHooks/useApi";

const getToday = () => new Date().toISOString().split("T")[0];

const MasterTransfertoDistributorTable = ({ bid, refreshKey, selectedMerchant }) => {
    const { callApi } = useApi("/ledger/retailer", "GET");

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [fromDate, setFromDate] = useState(getToday());
    const [toDate, setToDate] = useState(getToday());

    const fetchLedger = async (targetPage = 0, loadMore = false) => {
        if (!bid) return;

        setLoading(true);

        try {
            const params = new URLSearchParams();

            params.append("bid", bid?.trim());
            params.append("page", targetPage);
            params.append("size", size);
            params.append("scope", "self");
            params.append("feature", "DISTRIBUTOR_SELF_BUSINESS_CASH_OUT");

            if (fromDate) params.append("fromDate", fromDate);
            if (toDate) params.append("toDate", toDate);

            const query = `?${params.toString()}`;

            const res = await callApi(null, query);

            const content = res?.content || [];

            setTotalPages(res?.totalPages || 0);
            setTotalElements(res?.totalElements || 0);

            let filteredContent = content;

            if (selectedMerchant?.name) {
                filteredContent = content.filter(row =>
                    row?.narration?.toLowerCase().includes(
                        selectedMerchant.name.toLowerCase()
                    )
                );
            }

            setData(prev =>
                loadMore ? [...prev, ...filteredContent] : filteredContent
            );



            setPage(targetPage);

        } catch (err) {
            console.error("Ledger API failed", err);
            if (!loadMore) setData([]);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchLedger(0, false);
    }, [bid, size, refreshKey, selectedMerchant]);

    const handleLoadMore = () => {
        if (page + 1 < totalPages) {
            fetchLedger(page + 1, true);
        }
    };

    const totalDebit = data.reduce(
        (sum, row) =>
            row.txnType === "DEBIT"
                ? sum + Number(row.txnAmount || 0)
                : sum,
        0
    );

    return (
        <div className="bg-white rounded-xl shadow border p-4 space-y-4">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">

                {/* LEFT FILTER SECTION */}
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-5 w-full md:w-auto">

                    {/* FROM DATE */}
                    <div className="flex flex-col w-full sm:w-auto">
                        <label className="text-xs md:text-sm text-gray-500 mb-1">
                            From Date
                        </label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="h-11 md:h-12 px-4 border rounded-xl text-sm md:text-base focus:ring-2 focus:ring-indigo-400 outline-none"
                        />
                    </div>

                    {/* TO DATE */}
                    <div className="flex flex-col w-full sm:w-auto">
                        <label className="text-xs md:text-sm text-gray-500 mb-1">
                            To Date
                        </label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="h-11 md:h-12 px-4 border rounded-xl text-sm md:text-base focus:ring-2 focus:ring-indigo-400 outline-none"
                        />
                    </div>

                    {/* SEARCH BUTTON */}
                    <div className="flex flex-col w-full sm:w-auto justify-end">
                        <button
                            onClick={() => fetchLedger(0, false)}
                            className="h-11 md:h-12 px-8 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm md:text-base font-semibold transition"
                        >
                            Search
                        </button>
                    </div>

                    {/* ROW SELECT */}
                    <div className="flex flex-col w-full sm:w-auto">
                        <label className="text-xs md:text-sm text-gray-500 mb-1">
                            Rows
                        </label>
                        <select
                            value={size}
                            onChange={(e) => setSize(Number(e.target.value))}
                            className="h-11 md:h-12 px-4 border rounded-xl text-sm md:text-base focus:ring-2 focus:ring-indigo-400 outline-none"
                        >
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                        </select>
                    </div>

                </div>

                {/* RIGHT SUMMARY SECTION */}
                <div className="bg-gray-50 md:bg-transparent rounded-xl md:rounded-none p-4 md:p-0 w-full md:w-auto">

                    <div className="flex justify-between md:flex-col md:items-end gap-2">

                        <div className="font-bold text-red-600 text-lg md:text-xl">
                            Total Debit: ₹{totalDebit.toFixed(2)}
                        </div>

                        <div className="text-gray-700 text-sm md:text-base">
                            Total Records: <b>{totalElements}</b>
                        </div>

                    </div>

                </div>

            </div>


            {/* TABLE */}
            {loading && page === 0 ? (
                <div className="p-6 text-center">Loading...</div>
            ) : data.length === 0 ? (
                <div className="p-6 text-center text-gray-400">
                    No Records
                </div>
            ) : (
                <>
                    {/* ================= DESKTOP TABLE ================= */}
                    {/* ================= COMMON LIST VIEW (ALL DEVICES) ================= */}
                    <div className="mt-4 divide-y divide-gray-200">

                        {data.map((row, i) => {
                            const isDebit = row.txnType === "DEBIT";

                            return (
                                <div
                                    key={i}
                                    className="py-5 px-2 md:px-4 hover:bg-gray-50 transition"
                                >
                                    <div className="flex items-start gap-4">

                                        {/* ICON */}
                                        <div
                                            className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${isDebit ? "bg-red-50" : "bg-green-50"
                                                }`}
                                        >
                                            <span
                                                className={`text-lg font-semibold ${isDebit ? "text-red-500" : "text-green-600"
                                                    }`}
                                            >
                                                {isDebit ? "↗" : "↙"}
                                            </span>
                                        </div>

                                        {/* LEFT CONTENT */}
                                        <div className="flex-1 min-w-0">

                                            <div className="flex justify-between items-start gap-3">

                                                <div className="min-w-0">
                                                    <div className="text-sm text-gray-500">
                                                        {isDebit ? "Paid to" : "Received from"}
                                                    </div>

                                                    <div className="text-base md:text-lg font-semibold text-gray-800 break-words">
                                                        {row.narration || "Transaction"}
                                                    </div>

                                                    <div className="text-xs text-gray-400 mt-1">
                                                        {new Date(row.txnDate).toLocaleString("en-IN")}
                                                    </div>

                                                    <div className="text-xs text-gray-400 mt-1 break-all font-mono">
                                                        {row.txnId}
                                                    </div>
                                                </div>

                                                {/* AMOUNT */}
                                                <div
                                                    className={`text-lg md:text-xl font-bold whitespace-nowrap shrink-0 ${isDebit ? "text-red-500" : "text-green-600"
                                                        }`}
                                                >
                                                    {isDebit
                                                        ? `- ₹${Number(row.txnAmount).toFixed(2)}`
                                                        : `+ ₹${Number(row.txnAmount).toFixed(2)}`
                                                    }
                                                </div>

                                            </div>

                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                    </div>

                    {/* ================= MOBILE LIST VIEW ================= */}
                    <div className="md:hidden -mx-4 px-4 bg-white">
                        {data.map((row, i) => {
                            const isDebit = row.txnType === "DEBIT";

                            return (
                                <div
                                    key={i}
                                    className="py-4 border-b border-gray-200 last:border-none"
                                >
                                    <div className="flex justify-between items-start">

                                        {/* LEFT SECTION */}
                                        <div className="flex gap-3">

                                            {/* ICON CIRCLE */}
                                            <div className={`w-11 h-11 rounded-full flex items-center justify-center ${isDebit ? "bg-red-50" : "bg-green-50"
                                                }`}>
                                                <span className={`text-lg font-semibold ${isDebit ? "text-red-500" : "text-green-600"
                                                    }`}>
                                                    {isDebit ? "↗" : "↙"}
                                                </span>
                                            </div>

                                            {/* TEXT CONTENT */}
                                            <div>
                                                <div className="text-xs text-gray-500">
                                                    {isDebit ? "Paid to" : "Received from"}
                                                </div>

                                                <div className="text-base font-semibold text-gray-800 leading-snug">
                                                    {row.narration || "Transaction"}
                                                </div>

                                                <div className="text-xs text-gray-400 mt-1">
                                                    {new Date(row.txnDate).toLocaleDateString("en-IN")}
                                                </div>

                                                <div className="text-[11px] text-gray-400 mt-1 break-all">
                                                    {row.txnId}
                                                </div>
                                            </div>
                                        </div>

                                        {/* AMOUNT */}
                                        <div className={`text-base font-bold ${isDebit ? "text-red-500" : "text-green-600"
                                            }`}>
                                            {isDebit
                                                ? `- ₹${Number(row.txnAmount).toFixed(2)}`
                                                : `+ ₹${Number(row.txnAmount).toFixed(2)}`
                                            }
                                        </div>

                                    </div>
                                </div>
                            );
                        })}
                    </div>



                    {/* LOAD MORE */}
                    {page + 1 < totalPages && (
                        <div className="flex justify-center pt-3">
                            <button
                                onClick={handleLoadMore}
                                disabled={loading}
                                className="px-6 py-2 bg-gray-900 text-white rounded"
                            >
                                {loading ? "Loading..." : "Load More"}
                            </button>
                        </div>
                    )}
                </>
            )}

        </div>
    );
};

export default MasterTransfertoDistributorTable;
